import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
} from "@mui/material";
import { InscricoesEtapaList } from "components/InscricoesEtapaList";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { IInscricao } from "shared/features/api/inscricao/types";
import { toast } from "react-toastify";
import { SimuladorNotaCorte } from "components/PaineisAvaliacaoEtapas/SimuladorNotaCorte";
import { Etapa } from "shared/features/api/edicao/edicaoSlice";
import { getAvaliacaoInscricaoPorEtapaExcel } from "shared/features/api/export";
import { AxiosErrorWithData } from "shared/interfaces";
import { useRef, useState } from "react";
import {
  useAvaliarTodasInscricoesEtapaMutation,
  useImportarAvaliacoesMutation,
} from "shared/features/api/avaliacao/avaliacaoSlice";
import { InfoSquare } from "components/InfoSquare";

export interface PainelBaseProps {
  etapa: Etapa;
  inscricoes?: IInscricao;
  avaliarTodas?: () => Promise<void>;
  isLoading: boolean;
  setSearch: (value: string) => void;
  page: number;
  setPage: (page: number) => void;
  statusEtapa: string;
  setStatusEtapa: (valor: string) => void;
  trilha: string;
  setTrilha: (valor: string) => void;
}

export const PainelEtapaBase: React.FC<PainelBaseProps> = ({
  etapa,
  inscricoes,
  avaliarTodas,
  isLoading,
  setSearch,
  page,
  setPage,
  statusEtapa,
  setStatusEtapa,
  trilha,
  setTrilha,
}) => {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importarAvaliacoes] = useImportarAvaliacoesMutation();
  const [avaliarTodasGenerico] = useAvaliarTodasInscricoesEtapaMutation();

  const handleAvaliarTodas = async () => {
    if (avaliarTodas) {
      await toast.promise(avaliarTodas(), {
        pending: "Carregando...",
        success: `Inscrições na etapa ${etapa.nome} avaliadas com sucesso!`,
        error: {
          render({ data }) {
            const response = data as {
              data?: {
                errors?: string[];
                message?: string;
              };
            };
            const errorMessage =
              response.data?.errors?.[0] ||
              response.data?.message ||
              "Erro ao avaliar inscrições.";
            return errorMessage;
          },
        },
      });
    } else {
      await toast.promise(avaliarTodasGenerico(etapa.idEtapa), {
        pending: "Carregando...",
        success: `Inscrições na etapa ${etapa.nome} avaliadas com sucesso!`,
        error: {
          render({ data }) {
            const response = data as {
              data?: {
                errors?: string[];
                message?: string;
              };
            };
            const errorMessage =
              response.data?.errors?.[0] ||
              response.data?.message ||
              "Erro ao avaliar inscrições.";
            return errorMessage;
          },
        },
      });
    }
  };

  const handleExportar = async () => {
    await toast.promise(getAvaliacaoInscricaoPorEtapaExcel(etapa.idEtapa), {
      pending: "Carregando...",
      success: "Inscrições exportadas com sucesso!",
      error: {
        render({ data }) {
          const err = data as AxiosErrorWithData;

          if (err.response?.status === 400) {
            return "Nenhuma inscrição está em andamento na etapa procurada.";
          }

          return (
            err.response?.data?.errors?.[0] ||
            err.response?.data?.message ||
            "Erro ao exportar inscrições da etapa."
          );
        },
      },
    });
  };

  const getTotal = (status: string): number => {
    return (
      inscricoes?.elementos.filter((insc) =>
        insc.inscricaoEtapas.some(
          (e) => e.nomeEtapa === etapa.nome && e.statusEtapa === status
        )
      ).length ?? 0
    );
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const payloadImportar = {
      arquivo: file,
      idEtapa: etapa.idEtapa,
    };

    await toast.promise(importarAvaliacoes(payloadImportar).unwrap(), {
      pending: "Carregando...",
      success: "Avaliações importadas com sucesso!",
      error: "Erro ao importar as avaliações",
    });
    setOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          flexDirection: "column",
          gap: { xs: 2, md: 2 },
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            width: "100%",
          }}
        >
          <Paper sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InfoSquare titulo="Em andamento" dado={getTotal("ANDAMENTO")} />
              <InfoSquare titulo="Aprovados" dado={getTotal("APROVADO")} />
              <InfoSquare titulo="Reprovados" dado={getTotal("REPROVADO")} />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "center",
                marginTop: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ListAltOutlinedIcon color="primary" />}
                fullWidth
                sx={{ height: "80%", minHeight: 36, py: 0.5 }}
                onClick={handleAvaliarTodas}
                disabled={isLoading}
              >
                AVALIAR
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileUploadOutlinedIcon color="primary" />}
                fullWidth
                sx={{ height: "80%", minHeight: 36, py: 0.5 }}
                onClick={() => handleExportar()}
              >
                EXPORTAR
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon color="primary" />}
                fullWidth
                sx={{ height: "80%", minHeight: 36, py: 0.5 }}
                onClick={() => setOpen(true)}
              >
                IMPORTAR
              </Button>
            </Box>
          </Paper>

          <SimuladorNotaCorte inscricoes={inscricoes!} etapaNome={etapa.nome} />
        </Box>

        {inscricoes && (
          <Box sx={{ width: "100%", mt: { xs: 2, md: 0 } }}>
            <InscricoesEtapaList
              inscricoes={inscricoes}
              etapa={etapa.nome}
              setSearch={setSearch}
              page={page}
              setPage={setPage}
              setStatusEtapa={setStatusEtapa}
              statusEtapa={statusEtapa}
              trilha={trilha}
              setTrilha={setTrilha}
            />
          </Box>
        )}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Importar avaliações</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ao importar avaliações você ira atualizar a nota, observação e o
            status das inscrições para esta etapa.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
                fileInputRef.current.click();
              }
            }}
            color="primary"
            variant="contained"
          >
            Importar
          </Button>
        </DialogActions>
      </Dialog>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".xlsx,.xls,.csv"
      />
    </>
  );
};
